import { apiGet, apiPost, apiPut, apiDelete, apiGetBlob, apiPostFormData } from '@/shared/api/apiClient';
import type { AssetImportResult } from '../types/asset.types';
import {
  AssetTypeDto,
  AssetCategoryDto,
  AssetSpecFieldDto,
  AssetDto,
  AssetVariantDto,
  AssetAllocationDto,
  AssetPagedResult,
  RegisterAssetCommand,
  UpdateAssetCommand,
  CreateAssetVariantCommand,
  UpdateAssetVariantCommand,
  AssignVariantCommand,
  AssetFilterParams,
  AssignAssetCommand,
  ReturnAssetCommand,
  BulkAssignCommand,
  CreateAssetTypeCommand,
  UpdateAssetTypeCommand,
  CreateAssetCategoryCommand,
  UpdateAssetCategoryCommand,
  UpsertSpecFieldItem,
} from '../types/asset.types';

// ── Asset Types ──────────────────────────────────────────────────────────────

export const getAssetTypes = (categoryId?: string): Promise<AssetTypeDto[]> => {
  const qs = categoryId ? `?categoryId=${categoryId}` : '';
  return apiGet<AssetTypeDto[]>(`/assets/types${qs}`);
};

export const getAssetTypeById = (id: string, includeSpecs = false): Promise<AssetTypeDto> =>
  apiGet<AssetTypeDto>(`/assets/types/${id}${includeSpecs ? '?includeSpecs=true' : ''}`);

export const createAssetType = (
  command: CreateAssetTypeCommand
): Promise<{ id: string; message: string }> =>
  apiPost<{ id: string; message: string }>('/assets/types', command);

export const updateAssetType = (
  id: string,
  command: UpdateAssetTypeCommand
): Promise<{ message: string }> =>
  apiPut<{ message: string }>(`/assets/types/${id}`, command);

export const toggleAssetType = (id: string): Promise<{ message: string; isActive: boolean }> =>
  apiPut<{ message: string; isActive: boolean }>(`/assets/types/${id}/toggle`, {});

export const deleteAssetType = (id: string): Promise<void> =>
  apiDelete<void>(`/assets/types/${id}`);

// ── Asset Type Spec Fields ───────────────────────────────────────────────────

export const getAssetTypeSpecFields = (typeId: string): Promise<AssetSpecFieldDto[]> =>
  apiGet<AssetSpecFieldDto[]>(`/assets/types/${typeId}/spec-fields`);

export const upsertAssetTypeSpecFields = (
  typeId: string,
  fields: UpsertSpecFieldItem[]
): Promise<AssetSpecFieldDto[]> =>
  apiPut<AssetSpecFieldDto[]>(`/assets/types/${typeId}/spec-fields`, fields);

export const deleteAssetTypeSpecField = (typeId: string, fieldId: string): Promise<void> =>
  apiDelete<void>(`/assets/types/${typeId}/spec-fields/${fieldId}`);

// ── Asset Categories ─────────────────────────────────────────────────────────

export const getAssetCategories = (): Promise<AssetCategoryDto[]> =>
  apiGet<AssetCategoryDto[]>('/assets/categories');

export const getAssetCategoryById = (id: string): Promise<AssetCategoryDto> =>
  apiGet<AssetCategoryDto>(`/assets/categories/${id}`);

export const createAssetCategory = (
  command: CreateAssetCategoryCommand
): Promise<{ id: string; message: string }> =>
  apiPost<{ id: string; message: string }>('/assets/categories', command);

export const updateAssetCategory = (
  id: string,
  command: UpdateAssetCategoryCommand
): Promise<{ message: string }> =>
  apiPut<{ message: string }>(`/assets/categories/${id}`, command);

export const toggleAssetCategory = (id: string): Promise<{ message: string; isActive: boolean }> =>
  apiPut<{ message: string; isActive: boolean }>(`/assets/categories/${id}/toggle`, {});

export const deleteAssetCategory = (id: string): Promise<void> =>
  apiDelete<void>(`/assets/categories/${id}`);

// ── Asset Inventory ──────────────────────────────────────────────────────────

export const getAssets = (params: AssetFilterParams = {}): Promise<AssetPagedResult> => {
  const query = new URLSearchParams();
  if (params.assetTypeId) query.set('assetTypeId', params.assetTypeId);
  if (params.assetCategoryId) query.set('assetCategoryId', params.assetCategoryId);
  if (params.status != null) query.set('status', String(params.status));
  if (params.assignedToEmployeeId) query.set('assignedToEmployeeId', params.assignedToEmployeeId);
  if (params.search) query.set('search', params.search);
  if (params.page != null) query.set('page', String(params.page));
  if (params.pageSize != null) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  return apiGet<AssetPagedResult>(`/assets${qs ? `?${qs}` : ''}`);
};

export const getAssetById = (id: string): Promise<AssetDto> =>
  apiGet<AssetDto>(`/assets/${id}`);

export const registerAsset = (
  command: RegisterAssetCommand
): Promise<{ id: string }> =>
  apiPost<{ id: string }>('/assets', command);

export const updateAsset = (
  id: string,
  command: UpdateAssetCommand
): Promise<void> =>
  apiPut<void>(`/assets/${id}`, command);

export const deleteAsset = (id: string): Promise<void> =>
  apiDelete<void>(`/assets/${id}`);

// ── Asset Allocation ─────────────────────────────────────────────────────────

export const assignAsset = (
  assetId: string,
  command: AssignAssetCommand
): Promise<{ id: string; message: string }> =>
  apiPost(`/assets/${assetId}/assign`, command);

export const returnAsset = (
  assetId: string,
  command: ReturnAssetCommand
): Promise<{ message: string }> =>
  apiPost(`/assets/${assetId}/return`, command);

export const bulkAssignAssets = (
  command: BulkAssignCommand
): Promise<{ assigned: number; message: string }> =>
  apiPost('/assets/bulk-assign', command);

export const getAssetAllocations = (assetId: string): Promise<AssetAllocationDto[]> =>
  apiGet<AssetAllocationDto[]>(`/assets/${assetId}/allocations`);

export const getEmployeeAllocations = (
  employeeId: string,
  activeOnly = true
): Promise<AssetAllocationDto[]> =>
  apiGet<AssetAllocationDto[]>(`/assets/employee/${employeeId}/allocations?activeOnly=${activeOnly}`);

// ── Asset Variants ───────────────────────────────────────────────────────────

export const getAssetVariants = (assetId: string): Promise<AssetVariantDto[]> =>
  apiGet<AssetVariantDto[]>(`/assets/${assetId}/variants`);

export const createAssetVariant = (
  assetId: string,
  command: CreateAssetVariantCommand
): Promise<{ id: string; variantCode: string }> =>
  apiPost<{ id: string; variantCode: string }>(`/assets/${assetId}/variants`, command);

export const updateAssetVariant = (
  assetId: string,
  variantId: string,
  command: UpdateAssetVariantCommand
): Promise<void> =>
  apiPut<void>(`/assets/${assetId}/variants/${variantId}`, command);

export const deleteAssetVariant = (assetId: string, variantId: string): Promise<void> =>
  apiDelete<void>(`/assets/${assetId}/variants/${variantId}`);

export const assignVariant = (
  assetId: string,
  variantId: string,
  command: AssignVariantCommand
): Promise<{ id: string; message: string }> =>
  apiPost(`/assets/${assetId}/variants/${variantId}/assign`, command);

export const returnVariant = (assetId: string, variantId: string): Promise<{ message: string }> =>
  apiPost(`/assets/${assetId}/variants/${variantId}/return`, {});

// ── Asset Import ─────────────────────────────────────────────────────────────

export const downloadAssetImportTemplate = (): Promise<Blob> =>
  apiGetBlob('/assets/import/template');

export const importAssets = (file: File): Promise<AssetImportResult> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiPostFormData<AssetImportResult>('/assets/import', formData);
};

// ── Access ───────────────────────────────────────────────────────────────────

/**
 * Whether the signed-in user is on the API's Assets:AllowedEmails list. The list lives
 * only in appsettings.json — this endpoint is the single place the SPA learns the answer.
 */
export const getAssetsAccess = (): Promise<boolean> =>
  apiGet<{ allowed: boolean }>('/assets/access').then(r => r.allowed);
